import { useState, useRef, useCallback, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, ChevronLeft, ChevronRight, Download, Save, ArrowLeft, PenLine, X } from "lucide-react";
import { PDFDocument, rgb } from "pdf-lib";
import { toast } from "sonner";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const PDF_PAGE_WIDTH = 612;
const PDF_PAGE_HEIGHT = 792;
const RENDER_WIDTH = 816;

function pdfToCSS(rect, fieldType) {
  const [x1, y1, x2, y2] = rect;
  // Text/date fields need a small upward shift to align baselines with PDF text
  const yShift = (fieldType === "text" || fieldType === "date") ? 4 : 0;
  return {
    left: `${(x1 / PDF_PAGE_WIDTH) * 100}%`,
    top: `${((PDF_PAGE_HEIGHT - y2 - yShift) / PDF_PAGE_HEIGHT) * 100}%`,
    width: `${((x2 - x1) / PDF_PAGE_WIDTH) * 100}%`,
    height: `${((y2 - y1) / PDF_PAGE_HEIGHT) * 100}%`,
  };
}

function FieldOverlay({ field, value, onChange, onSignatureClick }) {
  const pos = pdfToCSS(field.rect, field.type);

  if (field.type === "checkbox") {
    return (
      <div
        className="absolute flex items-center justify-center cursor-pointer"
        style={{ ...pos, zIndex: 10 }}
        onClick={() => onChange(value === "on" ? "" : "on")}
        data-testid={`field-${field.id}`}
      >
        <div className={`w-full h-full border-2 rounded-sm flex items-center justify-center transition-colors ${value === "on" ? "bg-blue-500 border-blue-600" : "bg-white/80 border-blue-300 hover:border-blue-400"}`}>
          {value === "on" && <span className="text-white text-[8px] font-bold leading-none">X</span>}
        </div>
      </div>
    );
  }

  if (field.type === "signature") {
    return (
      <div
        className="absolute cursor-pointer group"
        style={{ ...pos, zIndex: 10 }}
        onClick={() => onSignatureClick(field.id)}
        data-testid={`field-${field.id}`}
      >
        {value ? (
          <img src={value} alt="signature" className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full border-2 border-dashed border-amber-400 bg-amber-50/60 rounded flex items-center justify-center gap-1 group-hover:bg-amber-100/80 transition-colors">
            <PenLine className="w-3 h-3 text-amber-600" />
            <span className="text-[9px] text-amber-700 font-medium">Sign</span>
          </div>
        )}
      </div>
    );
  }

  // Text or date field
  return (
    <div className="absolute" style={{ ...pos, zIndex: 10 }}>
      <input
        type={field.type === "date" ? "date" : "text"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.label || ""}
        className="w-full h-full bg-blue-50/60 rounded-sm px-1 text-[10px] border border-transparent hover:bg-blue-100/70 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-300 outline-none transition-colors placeholder:text-blue-300/70"
        style={{ fontSize: "clamp(8px, 1.1vw, 11px)" }}
        data-testid={`field-${field.id}`}
      />
    </div>
  );
}

export function PDFFormFiller({
  pdfUrl,
  displayPdfUrl,
  fields,
  title,
  subtitle,
  docType,
  onSave,
  onBack,
  pdfFieldMapping,
  manualTextMapping,
}) {
  const renderUrl = displayPdfUrl || pdfUrl;
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [values, setValues] = useState(() => {
    const init = {};
    fields.forEach((f) => { init[f.id] = f.defaultValue || ""; });
    return init;
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sigDialogOpen, setSigDialogOpen] = useState(false);
  const [activeSignatureField, setActiveSignatureField] = useState(null);
  const sigRef = useRef(null);

  const handleChange = useCallback((fieldId, value) => {
    setValues((p) => ({ ...p, [fieldId]: value }));
  }, []);

  const handleSignatureClick = useCallback((fieldId) => {
    setActiveSignatureField(fieldId);
    setSigDialogOpen(true);
  }, []);

  const handleSignatureSave = useCallback(() => {
    if (sigRef.current && activeSignatureField) {
      const dataUrl = sigRef.current.toDataURL("image/png");
      setValues((p) => ({ ...p, [activeSignatureField]: dataUrl }));
    }
    setSigDialogOpen(false);
    setActiveSignatureField(null);
  }, [activeSignatureField]);

  const currentPageFields = fields.filter((f) => f.page === currentPage - 1);

  const generateFilledPDF = async () => {
    const response = await fetch(pdfUrl);
    const pdfBytes = await response.arrayBuffer();
    const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });

    // Fill form fields if mapping provided
    if (pdfFieldMapping) {
      try {
        const form = pdfDoc.getForm();
        for (const [fieldId, pdfFieldName] of Object.entries(pdfFieldMapping)) {
          const val = values[fieldId];
          if (!val) continue;
          try {
            const textField = form.getTextField(pdfFieldName);
            textField.setText(val);
          } catch {
            try {
              const cb = form.getCheckBox(pdfFieldName);
              if (val === "on") cb.check(); else cb.uncheck();
            } catch { /* field not found or wrong type */ }
          }
        }
        form.flatten();
      } catch (e) {
        console.warn("Form filling error:", e);
      }
    }

    // Draw manual text if provided
    if (manualTextMapping) {
      for (const mapping of manualTextMapping) {
        const val = values[mapping.fieldId];
        if (!val) continue;
        const page = pdfDoc.getPages()[mapping.page];
        if (!page) continue;
        page.drawText(val, {
          x: mapping.x,
          y: mapping.y,
          size: mapping.fontSize || 10,
          color: rgb(0, 0, 0),
        });
      }
    }

    // Draw signatures
    for (const field of fields) {
      if (field.type === "signature" && values[field.id]) {
        try {
          const sigData = values[field.id];
          const sigImage = await pdfDoc.embedPng(sigData);
          const page = pdfDoc.getPages()[field.page];
          const [x1, y1, x2, y2] = field.rect;
          page.drawImage(sigImage, {
            x: x1,
            y: y1,
            width: x2 - x1,
            height: y2 - y1,
          });
        } catch (e) {
          console.warn("Signature embed error:", e);
        }
      }
    }

    return await pdfDoc.save();
  };

  const handleDownload = async () => {
    try {
      const bytes = await generateFilledPDF();
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${docType}_filled.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate PDF");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const bytes = await generateFilledPDF();
      const blob = new Blob([bytes], { type: "application/pdf" });
      const file = new File([blob], `${docType}_filled.pdf`, { type: "application/pdf" });
      await onSave(file, docType);
      toast.success("Document saved");
    } catch (e) {
      console.error(e);
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  const pageFieldCounts = {};
  fields.forEach((f) => {
    pageFieldCounts[f.page + 1] = (pageFieldCounts[f.page + 1] || 0) + 1;
  });

  return (
    <div className="max-w-5xl mx-auto" data-testid={`pdf-form-filler-${docType}`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="pdf-filler-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading font-bold text-xl text-navy">{title}</h1>
          {subtitle && <p className="text-slate-500 font-body text-xs">{subtitle}</p>}
        </div>
      </div>

      {/* Page navigation */}
      {numPages && (
        <div className="flex items-center justify-between bg-slate-900 text-white rounded-t-lg px-4 py-2 text-sm">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost" size="sm"
              className="text-white hover:bg-slate-700 h-7 px-2"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              data-testid="pdf-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-mono text-xs">
              Page {currentPage} of {numPages}
            </span>
            <Button
              variant="ghost" size="sm"
              className="text-white hover:bg-slate-700 h-7 px-2"
              disabled={currentPage >= numPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              data-testid="pdf-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center gap-1">
            {currentPageFields.length > 0 && (
              <span className="bg-blue-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium">
                {currentPageFields.length} fields on this page
              </span>
            )}
            {/* Page dots for pages with fields */}
            <div className="flex gap-1 ml-3">
              {Array.from({ length: numPages }, (_, i) => i + 1).map((pg) => (
                <button
                  key={pg}
                  onClick={() => setCurrentPage(pg)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    pg === currentPage
                      ? "bg-white"
                      : pageFieldCounts[pg]
                        ? "bg-blue-400"
                        : "bg-slate-600"
                  }`}
                  title={`Page ${pg}${pageFieldCounts[pg] ? ` (${pageFieldCounts[pg]} fields)` : ""}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer */}
      <div className="border border-slate-300 bg-slate-100 rounded-b-lg overflow-hidden relative" style={{ minHeight: 400 }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        )}
        <div className="flex justify-center">
          <div className="relative inline-block" style={{ width: RENDER_WIDTH }}>
            <Document
              file={renderUrl}
              onLoadSuccess={({ numPages: n }) => { setNumPages(n); setLoading(false); }}
              onLoadError={() => { setLoading(false); toast.error("Failed to load PDF"); }}
              loading=""
            >
              <Page
                pageNumber={currentPage}
                width={RENDER_WIDTH}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
            {/* Field overlays */}
            {currentPageFields.map((field) => (
              <FieldOverlay
                key={field.id}
                field={field}
                value={values[field.id]}
                onChange={(v) => handleChange(field.id, v)}
                onSignatureClick={handleSignatureClick}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
        <Button variant="outline" onClick={handleDownload} className="flex-1" data-testid="pdf-download-btn">
          <Download className="w-4 h-4 mr-2" /> Download Filled PDF
        </Button>
        <Button onClick={handleSave} disabled={saving} className="flex-1 bg-teal hover:bg-teal-dark text-white" data-testid="pdf-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save to My Documents
        </Button>
      </div>

      {/* Signature Dialog */}
      <Dialog open={sigDialogOpen} onOpenChange={setSigDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Draw Your Signature</DialogTitle>
          </DialogHeader>
          <div className="border-2 border-slate-300 rounded-lg bg-white">
            <SignatureCanvas
              ref={sigRef}
              penColor="black"
              canvasProps={{ width: 400, height: 160, className: "w-full" }}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => sigRef.current?.clear()}>
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
            <Button size="sm" onClick={handleSignatureSave} className="bg-teal hover:bg-teal-dark text-white" data-testid="signature-save-btn">
              <PenLine className="w-3 h-3 mr-1" /> Apply Signature
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
